import {
  Component,
  Prop,
  Event,
  EventEmitter,
  State,
  h,
  Element,
  Method,
  Listen,
} from "@stencil/core";
import { debounce } from "throttle-debounce";
import { v4 as uuid } from "uuid";

import { HeaderInterface } from "../../services/header-interface/header-interface.service";

import {
  TCustomerSegment,
  TFusionAppName,
  TFusionInstance,
  TLanguage,
} from "../../types";
import {
  PopularSearchWord,
  SearchService,
} from "../../services/search/search.service";
import { suggestionConfiguration } from "./suggestion-configuration";
import { SearchSuggestionText } from "./SearchSuggestionText";
import { FusionApiVersion } from "../../models/search/fusion/fusion-versions";
import { Bundle, bundleForLanguage } from "../../services/i18n";
import {
  SearchSuggestionService,
  Suggestions,
} from "../../services/search/search-suggestion.service";
import { SearchTrackingService } from "../../services/search/search-tracking.service";
import { SearchSuggestion } from "../../models/search/suggestions/search-suggestion.model";
import { ProductSuggestion } from "../../models/search/suggestions/product-suggestion.model";
import { QuickLinkSuggestion } from "../../models/search/suggestions/quick-link-suggestion.model";
import { FusionSuggestionRequestOptions } from "../../models/search/fusion/fusion-suggestion-request-options.model";
import { SearchOptions } from "../../models/search/search-options.model";
import { timer } from "../../globals";
import { trackWithODL } from "lil-swisgom-hlepers";

export type Focus = "on" | "off";
export type Size = 1 | 2 | "auto";
export type SizeState = 1 | 2;
export type AutocompleteDirection = "right" | "left" | null;

const DEFAULT_SIZE: SizeState = 1;
const AUTOCOMPLETE_TRACKING_LABEL: string = "keystroke";
const RECENTLY_VIEWED_STORAGE_KEY = "scs-search-recently-viewed";
const RECENTLY_VIEWED_MAX_ITEMS = 5;

const SLOT_DATASET = {
  trackLocation: "header",
  trackAction: "open",
  trackCategory: "l1-icon",
  trackLabel: "search",
};

const setFullBleedSearchHeaderAsSoonAsAvailable = (): boolean => {
  const header = document.querySelector("sdx-header");
  if (
    header &&
    header.slots &&
    header.slots.length &&
    (header.slots as any).find &&
    (header.slots as any).find((s: any) => s.slot === "search")
  ) {
    const slots: any = [...header.slots];
    (slots!.find((s: any) => s.slot === "search") as any)!.menuWidth = "full";
    header.slots = slots;
    return true;
  } else {
    return false;
  }
};

(async () => {
  for (let i = 0; i < 1000; ++i) {
    const isFullbleedSet = setFullBleedSearchHeaderAsSoonAsAvailable();
    if (isFullbleedSet) {
      break;
    }
    await timer(50);
  }
})();

@Component({
  tag: "scs-search-input",
  styleUrls: ["search-input.scss"],
  shadow: true,
})
export class Search {
  private autocompleteFocusIndex: number = -1;
  private inputReferenceId!: string;
  private originalInputValue!: string;
  private searchService: SearchService;
  private searchSuggestionService: SearchSuggestionService;
  private searchTrackingService: SearchTrackingService;
  private suggestionLoading = false;
  private cancelSuggestionLoading = false;
  private autocompleteSearchTracked = false;
  private sdxHeader!: HeaderInterface;
  private fetchSuggestionsImmediate!: (searchTerm: string) => void;
  private autocompleteEl?: HTMLScsAutocompleteElement;
  private autocompleteReferenceId!: string;

  private i18n!: Bundle;

  @Element()
  private host!: HTMLScsSearchInputElement;

  /** optional: segment of logged in user, will be used for backend calls and to identify the correct default result page path */
  @Prop()
  public customerSegment: TCustomerSegment = "res";
  /** will be used for backend calls, translations and to identify the correct default result page path */
  @Prop()
  public language = (document.documentElement.lang || "de") as TLanguage;

  /** optional: defines if the component triggers search event instead of navigating to the SERP. If this option is `true` then properties `resultPagePath`, `queryParameter`, `hostUrl`, `customer-segment`, and `fusion{AppName|Instance|UserGroup}` are obsolete */
  @Prop()
  public eventBasedSearch: boolean = false;
  /** optional: custom result page path to navigate after search execution */
  @Prop()
  public resultPagePath?: string;
  /** optional: custom search query parameter, will be used for the search term */
  @Prop()
  public queryParameter: string = "query";
  /** optional: placeholder for search input field */
  @Prop()
  public placeholder: string | null = null;
  /** optional: host to use for fusion backend calls, use this only for testing reasons */
  @Prop()
  public hostUrl: string = "";
  /** optional: application name to use for fusion backend calls */
  // TODO Check if this is still being used... This looks like it worked in Fusion 4 but shouldn't in 5.
  @Prop()
  public fusionAppName: TFusionAppName = "swisscom_portal";
  /** optional: fusion instance to use for fusion backend calls*/
  @Prop()
  public fusionInstance?: TFusionInstance;
  /** optional: user group to use for fusion backend calls */
  @Prop()
  public fusionUserGroup: string = "";
  /** optional: fusion api version to use */
  @Prop()
  public fusionApiVersion: FusionApiVersion = "v3";
  /** optional: tracking location used for adobe analytics tracking calls */
  @Prop()
  public trackingLocation: string = "content";
  /** optional: defines if voice search button is visible */
  @Prop()
  public showVoiceSearch: boolean = false;

  /** optional: defines if input field is rendered with box shadow */
  @Prop()
  public boxShadow: boolean = false;

  /** optional: defines if input field margins should be removed */
  @Prop()
  public removeInputMargins: boolean = false;

  /** optional: forces the autocomplete overlay to be shown */
  @Prop()
  public enableOverlay: boolean = false;

  /** optional: puts the overlay into position absolute, so it doesn't use up space but lays itself over other elements */
  @Prop()
  public floatingOverlay: boolean = false;

  /** optional: current inputValue of the input field */
  @Prop({
    attribute: "value",
    mutable: true,
    reflect: true,
  })
  public inputValue: string = "";

  /** will be used for backend calls, based on it's value the results will be different */
  @Prop()
  public isUserLoggedIn: boolean = false;

  @Event()
  public voiceSearchClicked!: EventEmitter;
  @Event()
  public inputFocusChanged!: EventEmitter<Focus>;
  @Event()
  public search!: EventEmitter<string>;

  @State()
  private autocompleteOpen: boolean = false;
  @State()
  private _inputFocused: boolean = false;
  @State()
  private popularSearchTerms: PopularSearchWord[] = [];
  @State()
  private recentlyViewedTerms: string[] = [];

  @State()
  private _size: SizeState = DEFAULT_SIZE;
  @State()
  private _suggestions: Array<SearchSuggestion> = [];
  @State()
  private _productSuggestions: Array<ProductSuggestion> = [];
  @State()
  private _quickLinkSuggestions: Array<QuickLinkSuggestion> = [];

  @Method()
  public async performSearch(searchTerm: string): Promise<void> {
    this.executeSearch(searchTerm);
  }

  get suggestions(): Array<SearchSuggestion> {
    return this._suggestions.filter((_i, index) => {
      const maxItems = suggestionConfiguration.maxSuggestions.config["default"];

      return index < maxItems;
    });
  }

  get productSuggestions(): Array<ProductSuggestion> {
    return this._productSuggestions.filter(
      (_i, index) =>
        index <
        suggestionConfiguration.maxProductSuggestions["config"][
          !!this._quickLinkSuggestions.length ? "withQuickLinks" : "default"
        ],
    );
  }

  get quickLinkSuggestions(): Array<QuickLinkSuggestion> {
    return this._quickLinkSuggestions.filter(
      (_i, index) =>
        index <
        suggestionConfiguration.maxQuickLinkSuggestions["config"][
          !!this._productSuggestions.length ? "withProducts" : "default"
        ],
    );
  }

  private get hasSuggestions(): boolean {
    return this.suggestions && !!this.suggestions.length;
  }

  private get isHeaderSearch(): boolean {
    return (
      this.host.parentElement?.tagName?.toLocaleLowerCase() === "sdx-header"
    );
  }

  private get shouldRenderOverlay(): boolean {
    return (
      this.enableOverlay ||
      this.isHeaderSearch ||
      window.location.origin.includes("localhost")
    );
  }

  private get fusionSearchSuggestionRequestOptions(): FusionSuggestionRequestOptions {
    return {
      appName: this.fusionAppName,
      instance: this.fusionInstance,
      userGroup: this.fusionUserGroup,
      language: this.language,
      hostUrl: this.hostUrl,
      version: this.fusionApiVersion,
    };
  }

  private get searchOptions(): SearchOptions {
    return {
      customerSegment: this.customerSegment,
      hostUrl: this.hostUrl,
      language: this.language,
      queryParameter: this.queryParameter,
      resultPagePath: this.resultPagePath,
    };
  }

  constructor() {
    this.searchSuggestionService = SearchSuggestionService.getInstance();
    this.searchService = SearchService.getInstance();
    this.searchTrackingService = SearchTrackingService.getInstance();
  }

  activateVoiceSearchHandler(e: MouseEvent) {
    this.voiceSearchClicked.emit(e);
  }

  private loadRecentlyViewedTerms(): void {
    try {
      const stored = localStorage.getItem(RECENTLY_VIEWED_STORAGE_KEY);
      this.recentlyViewedTerms = stored ? JSON.parse(stored) : [];
    } catch {
      this.recentlyViewedTerms = [];
    }
  }

  private saveRecentlyViewedTerm(term: string): void {
    if (!term.trim()) return;
    try {
      const updated = [
        term,
        ...this.recentlyViewedTerms.filter(t => t !== term),
      ].slice(0, RECENTLY_VIEWED_MAX_ITEMS);
      localStorage.setItem(
        RECENTLY_VIEWED_STORAGE_KEY,
        JSON.stringify(updated),
      );
      this.recentlyViewedTerms = updated;
    } catch {
      // ignore storage errors
    }
  }

  private clearRecentlyViewed(): void {
    try {
      localStorage.removeItem(RECENTLY_VIEWED_STORAGE_KEY);
    } catch {
      // ignore storage errors
    }
    this.recentlyViewedTerms = [];
    trackWithODL({
      eventDetails: {
        type: "click",
        location: this.trackingLocation,
        category: "live-search",
        detail: "search-history-cleared",
      },
    });
  }

  executeSearch(searchTerm: string) {
    if (this.suggestionLoading) {
      this.cancelSuggestionLoading = true;
    }

    this.saveRecentlyViewedTerm(searchTerm);

    // SERP is using this, for search requests done while the app is already loaded.
    if (this.eventBasedSearch) {
      this.search.emit(searchTerm);
    } else {
      this.searchService.navigateBySearchTerm(searchTerm, this.searchOptions);
      this.host.assignedSlot?.closest("sdx-header-menu")?.close();
    }
  }

  fetchSuggestions(searchTerm: string) {
    this.searchSuggestionService
      .fetchSuggestions(
        searchTerm,
        this.fusionSearchSuggestionRequestOptions,
        this.isUserLoggedIn,
      )
      .then(
        ({
          productSuggestions,
          suggestions,
          quickLinkSuggestions,
        }: Suggestions) => {
          if (!this.cancelSuggestionLoading) {
            this._suggestions = suggestions;
            this._productSuggestions = productSuggestions;
            this._quickLinkSuggestions = quickLinkSuggestions;
            if (!this.autocompleteSearchTracked) {
              this.searchTrackingService.trackSuggestionSearch(
                AUTOCOMPLETE_TRACKING_LABEL,
                this.trackingLocation,
              );
              this.autocompleteSearchTracked = true;
            }
          } else {
            this.cancelSuggestionLoading = false;
          }
          this.suggestionLoading = false;
        },
      )
      .catch((error: unknown) => {
        console.error("[scs-search-input] Failed to fetch suggestions:", error);
        this.suggestionLoading = false;
      });
  }

  onSearchSuggestionSelect(suggestion: SearchSuggestion, index: number) {
    this.inputValue = suggestion.term;
    this.originalInputValue = suggestion.term;

    const position = index + 1;
    this.searchTrackingService.trackSuggestionSelection(
      this.inputValue,
      suggestion.term,
      position,
      this.trackingLocation,
    );
    if (!this.isHeaderSearch) {
      this.host.shadowRoot?.getElementById(this.inputReferenceId)?.blur();
    }
    this.executeSearch(suggestion.term);
  }

  onProductSuggestionSelect(product: ProductSuggestion) {
    this.searchTrackingService.trackWidgetSelection(
      this.inputValue,
      "products-widget",
      product.text,
      this.trackingLocation,
    );

    location.assign(product.href);
  }

  onQuickLinkSuggestionSelect(link: QuickLinkSuggestion) {
    this.searchTrackingService.trackWidgetSelection(
      this.inputValue,
      "quick-links-widget",
      link.text,
      this.trackingLocation,
    );

    location.assign(link.href);
  }

  autocompleteVisibilityChangeHandler(event: CustomEvent<boolean>) {
    this.autocompleteOpen = event.detail;
  }

  autocompleteFocusChangeHandler(event: CustomEvent<number>) {
    this.autocompleteFocusIndex = event.detail;

    if (this.isSuggestionFocused()) {
      this.inputValue =
        this.suggestions[this.autocompleteFocusIndex]?.term ?? this.inputValue;
    } else if (this.autocompleteOpen) {
      this.inputValue = this.originalInputValue;
    }
  }

  @Listen("focusin")
  inputFocusChangeHandler(): void {
    this.setInputFocused(true);
    if (this.inputValue && !this._suggestions.length) {
      this.originalInputValue = this.inputValue;
      this.suggestionLoading = true;
      this.fetchSuggestionsImmediate(this.inputValue);
    }
  }

  @Listen("focusout")
  inputBlurChangeHandler(event: FocusEvent): void {
    const relatedTarget = event.relatedTarget as Node | null;
    if (this.isFocusWithinComponent(relatedTarget)) {
      return;
    }
    if (this.inputValue) {
      this.originalInputValue = this.inputValue;
      this.suggestionLoading = true;
      this.fetchSuggestionsImmediate(this.inputValue);
    }
    this.setInputFocused(false);
  }

  private setInputFocused(focused: boolean): void {
    if (this._inputFocused === focused) {
      return;
    }
    this._inputFocused = focused;
    this.inputFocusChanged.emit(focused ? "on" : "off");
  }

  private isFocusWithinComponent(node: Node | null): boolean {
    if (!node) {
      return false;
    }
    return this.host === node || this.host.contains(node);
  }

  inputChangeHandler(event: Event): void {
    this.inputValue = (event.target as HTMLInputElement).value;
    this.originalInputValue = this.inputValue;
    this.suggestionLoading = true;
    if (!this.isHeaderSearch) {
      this.autocompleteEl?.open();
    }
    this.fetchSuggestions(this.inputValue);
  }

  inputKeydownHandler(event: KeyboardEvent): void {
    if (event.key === "Enter" && this.autocompleteFocusIndex === -1) {
      // only execute search if autocomplete isn't focused
      if (!this.isHeaderSearch) {
        (event.target as HTMLElement).blur();
      }
      this.searchTrackingService.trackSearch(
        this.inputValue,
        this.trackingLocation,
      );
      this.executeSearch(this.inputValue);
    }
  }

  isSuggestionFocused(): boolean {
    return (
      this.autocompleteFocusIndex !== -1 &&
      this.suggestions.length >= this.autocompleteFocusIndex + 1
    );
  }

  searchIconClickHandler(_event: MouseEvent): void {
    this.searchTrackingService.trackSearch(
      this.inputValue,
      this.trackingLocation,
    );
    this.executeSearch(this.inputValue);
  }

  popularSearchTermClickhander(term: string, url?: string): void {
    if (!this.isHeaderSearch) {
      this.host.shadowRoot?.getElementById(this.inputReferenceId)?.blur();
    }
    if (url && url.startsWith("http")) {
      this.searchTrackingService.trackSearch(url, this.trackingLocation);
      window.location.href = url;
    } else {
      this.searchTrackingService.trackSearch(term, this.trackingLocation);
      this.inputValue = term;
      this.originalInputValue = term;
      this.executeSearch(this.inputValue);
    }
  }

  widgetShownHandler(widgetType: string, results: number): void {
    if (!this.suggestionLoading) {
      this.searchTrackingService.trackWidgetDisplayed(
        this.inputValue,
        widgetType,
        this.trackingLocation,
        results,
      );
    }
  }

  async componentWillLoad(): Promise<void> {
    if (!this.host.slot) {
      this.host.slot = "search";
    }
    this.inputReferenceId = `input-${uuid()}`;
    this.autocompleteReferenceId = `autocomplete-${uuid()}`;
    this.fetchSuggestionsImmediate = this.fetchSuggestions.bind(this);
    this.fetchSuggestions = debounce(350, this.fetchSuggestions);
    this.sdxHeader = new HeaderInterface(this.host.closest("sdx-header")!);
    this.i18n = await bundleForLanguage(this.language);
    this.sdxHeader.updateSlot(this.host.slot, {
      label: this.i18n.t("SEARCH"),
      iconName: "icon-search",
      dataset: SLOT_DATASET,
      scrollable: false,
    });
    const shown = new window.IntersectionObserver(([entry]) => {
      if (entry?.isIntersecting) {
        this.host.shadowRoot?.getElementById(this.inputReferenceId)?.focus();
        shown.unobserve(this.host);
      }
    });
    shown.observe(this.host);
    this.popularSearchTerms = await this.searchService.getPopularSearchTerms(
      this.searchOptions.language,
    );
    this.loadRecentlyViewedTerms();

    if (this.inputValue) {
      this.originalInputValue = this.inputValue;
      this.fetchSuggestionsImmediate(this.inputValue);
    }
  }

  async componentWillUpdate(): Promise<void> {
    if (this.language !== this.i18n.lang) {
      this.i18n = await bundleForLanguage(this.language);
    }
  }

  getCssClasses() {
    return {
      searchRootCssClasses: {
        "input-margins": !this.removeInputMargins && !this.shouldRenderOverlay,
        "mobile-scroll": true,
        "floating-overlay": this.floatingOverlay,
      },
      wrapperCssClass: {
        wrapper: true,
        "wrapper--s1": this._size === 1,
        "wrapper--s2": this._size === 2,
        "search-root": true,
      },
      inputCssClass: {
        input: true,
        "input--box-shadow": this.boxShadow,
        "input--autocomplete-open":
          this.autocompleteOpen && this.hasSuggestions,
        "input--voice-search": this.showVoiceSearch,
        "floating-overlay": this.floatingOverlay,
      },
      autocompleteCssClass: {
        autocomplete: true,
        "floating-overlay": this.floatingOverlay,
      },
    };
  }

  render() {
    const {
      autocompleteCssClass,
      inputCssClass,
      wrapperCssClass,
      searchRootCssClasses,
    } = this.getCssClasses();

    return (
      <div class={searchRootCssClasses}>
        <div class={wrapperCssClass}>
          <div
            class="scs-search-input-container"
            role="search"
            aria-label={this.i18n.t("SEARCH_SITE_SEARCH")}
          >
            <input
              id={this.inputReferenceId}
              class={inputCssClass}
              type="search"
              placeholder={this.placeholder ?? this.i18n.t("SEARCH_SEARCH_FOR")}
              aria-label={this.placeholder ?? this.i18n.t("SEARCH_SEARCH_FOR")}
              aria-controls={this.autocompleteReferenceId}
              aria-autocomplete="list"
              aria-expanded={this.autocompleteOpen ? "true" : "false"}
              onKeyDown={event => this.inputKeydownHandler(event)}
              onInput={event => this.inputChangeHandler(event)}
              value={this.inputValue}
            />
            <div class="search-buttons">
              <sdx-button
                class="search-button"
                theme="transparent"
                onClick={(event: MouseEvent) =>
                  this.searchIconClickHandler(event)
                }
                srHint={this.i18n.t("SEARCH_START_SEARCH")}
                iconName="icon-search"
                iconSize={(this._size + 2) as 3 | 4}
              />
              {this.showVoiceSearch && (
                <sdx-button
                  class="voice-button"
                  theme="transparent"
                  onClick={(event: MouseEvent) =>
                    this.activateVoiceSearchHandler(event)
                  }
                  srHint={this.i18n.t("VOICE_SEARCH")}
                  iconName="icon-microphone"
                  iconSize={(this._size + 2) as 3 | 4}
                />
              )}
            </div>
            {this.shouldRenderOverlay &&
              (this.isHeaderSearch || this._inputFocused) && (
                <scs-autocomplete
                  id={this.autocompleteReferenceId}
                  class={autocompleteCssClass}
                  tabIndex={-1}
                  onPointerDown={(e: Event) => e.preventDefault()}
                  ref={el =>
                    (this.autocompleteEl = el as HTMLScsAutocompleteElement)
                  }
                  onAutocompleteFocusChanged={event =>
                    this.autocompleteFocusChangeHandler(event)
                  }
                  onAutocompleteVisibilityChanged={event =>
                    this.autocompleteVisibilityChangeHandler(event)
                  }
                  inputReferenceId={this.inputReferenceId}
                >
                  <div class="autocomplete-container">
                    <div class="suggestions-and-quick-links-container">
                      <div class="suggestions-container">
                        {!(this.suggestions && this.suggestions.length) &&
                          this.recentlyViewedTerms.length !== 0 && (
                            <div>
                              <div class="suggestion-type-title-row">
                                <h5 class="suggestion-type-title">
                                  {this.i18n.t("RECENTLY_VIEWED")}
                                </h5>
                                <sdx-button
                                  class="clear-recently-viewed"
                                  theme="transparent"
                                  icon-name="icon-bin"
                                  icon-size="1"
                                  label={this.i18n.t("RECENTLY_VIEWED_CLEAR")}
                                  sr-hint={this.i18n.t("RECENTLY_VIEWED_CLEAR")}
                                  onClick={() => this.clearRecentlyViewed()}
                                ></sdx-button>
                              </div>
                              <ul>
                                {this.recentlyViewedTerms.map(term => (
                                  <li class="popular-search-item">
                                    <sdx-button
                                      theme="transparent"
                                      icon-name="icon-arrow-right"
                                      icon-size="1"
                                      label={term}
                                      sr-hint={term}
                                      onClick={() =>
                                        this.popularSearchTermClickhander(term)
                                      }
                                    ></sdx-button>
                                  </li>
                                ))}
                              </ul>
                            </div>
                          )}

                        {!(this.suggestions && this.suggestions.length) &&
                          this.popularSearchTerms.length !== 0 && (
                            <h5 class="suggestion-type-title">
                              {this.i18n.t("POPULAR_SEARCH_TERMS")}
                            </h5>
                          )}

                        {!(this.suggestions && this.suggestions.length) &&
                          this.popularSearchTerms.length !== 0 && (
                            <ul>
                              {this.popularSearchTerms.map(
                                popularSearchTerm => (
                                  <li class="popular-search-item">
                                    <sdx-button
                                      theme="transparent"
                                      icon-name="icon-arrow-right"
                                      icon-size="1"
                                      label={popularSearchTerm.name}
                                      sr-hint={popularSearchTerm.name}
                                      onClick={() =>
                                        this.popularSearchTermClickhander(
                                          popularSearchTerm.name,
                                          popularSearchTerm.url,
                                        )
                                      }
                                    ></sdx-button>
                                  </li>
                                ),
                              )}
                            </ul>
                          )}

                        {this.hasSuggestions && (
                          <h5 class="suggestion-type-title">
                            {this.i18n.t("SEARCH_SUGGESTIONS")}
                          </h5>
                        )}

                        {this.suggestions.map(
                          (suggestion: SearchSuggestion, index: number) => (
                            <scs-autocomplete-option
                              tabIndex={-1}
                              class={"s" + this._size}
                              onAutocompleteOptionSelected={() =>
                                this.onSearchSuggestionSelect(suggestion, index)
                              }
                            >
                              <SearchSuggestionText
                                text={suggestion.term}
                                highlightText={this.originalInputValue}
                              ></SearchSuggestionText>
                            </scs-autocomplete-option>
                          ),
                        )}
                      </div>

                      {!!this.quickLinkSuggestions.length && (
                        <div class="quick-links-widget">
                          {this.widgetShownHandler(
                            "quick-links-widget",
                            this.quickLinkSuggestions.length,
                          )}

                          <h5 class="suggestion-type-title">
                            {this.i18n.t("QUICK_LINKS")}
                          </h5>
                          {this.quickLinkSuggestions.map(
                            (link: QuickLinkSuggestion) => (
                              <scs-autocomplete-quick-link-option
                                href={link.href}
                                appendix={link.appendix}
                                onAutocompleteOptionSelected={() =>
                                  this.onQuickLinkSuggestionSelect(link)
                                }
                              >
                                <SearchSuggestionText
                                  text={link.text}
                                  highlightText={this.originalInputValue}
                                ></SearchSuggestionText>
                              </scs-autocomplete-quick-link-option>
                            ),
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                </scs-autocomplete>
              )}
          </div>

          {this.shouldRenderOverlay &&
            (this.isHeaderSearch || this._inputFocused) &&
            !this.floatingOverlay &&
            !!this.productSuggestions.length && (
              <div class="product-widget">
                {this.widgetShownHandler(
                  "products-widget",
                  this.productSuggestions.length,
                )}
                <div class="product-item-container">
                  {this.productSuggestions.map(product => (
                    <scs-autocomplete-product-option
                      href={product.href}
                      name={product.text}
                      i18n={this.i18n}
                      imageUrl={product.img}
                      installmentPrice={product.installmentPrice}
                      retailPrice={product.retailPrice}
                      onAutocompleteOptionSelected={() =>
                        this.onProductSuggestionSelect(product)
                      }
                    >
                      <SearchSuggestionText
                        lineClamp={
                          product.installmentPrice || product.retailPrice
                            ? 1
                            : 2
                        }
                        text={product.text}
                        highlightText={this.originalInputValue}
                      ></SearchSuggestionText>
                    </scs-autocomplete-product-option>
                  ))}
                </div>
              </div>
            )}
        </div>
      </div>
    );
  }
}
